import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  AgentChatMessage,
  TAgentToolMessage,
} from '@/services/types/agent-chat-types';

import { Text } from '../../ui/catalyst/text';
import { Message } from './Message';

const AUTO_SCROLL_THRESHOLD = 300;
const USER_SCROLL_TIMEOUT = 150; // ms to wait after user stops scrolling

/**
 * Hook for smooth auto-scrolling during streaming content
 * Uses native CSS scroll-behavior: smooth for performance
 * Only scrolls if user is within threshold of bottom and not actively scrolling
 */
function useAutoScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const isUserScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollHeightRef = useRef(0);

  // Check if user is near the bottom
  const checkIsNearBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return true;

    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom < AUTO_SCROLL_THRESHOLD;
  }, []);

  // Scroll to bottom using native smooth scrolling
  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element || isUserScrollingRef.current) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  // Handle user scroll - update near-bottom status and track active scrolling
  const handleScroll = useCallback(() => {
    isNearBottomRef.current = checkIsNearBottom();

    // Mark user as actively scrolling
    isUserScrollingRef.current = true;

    // Clear any existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }

    // Set timeout to mark scrolling as stopped
    userScrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, USER_SCROLL_TIMEOUT);
  }, [checkIsNearBottom]);

  // Observe DOM mutations for streaming content changes
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    const observer = new MutationObserver(() => {
      const scrollHeightChanged =
        element.scrollHeight !== lastScrollHeightRef.current;

      if (
        isNearBottomRef.current &&
        !isUserScrollingRef.current &&
        scrollHeightChanged
      ) {
        lastScrollHeightRef.current = element.scrollHeight;
        scrollToBottom();
      }
    });

    observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [scrollToBottom]);

  return { scrollRef, handleScroll };
}

interface MessageListProps {
  messages: AgentChatMessage[];
  isLoading: boolean;
  matterId: string;
  threadId: string;
}

export const MessageList = ({
  messages,
  isLoading,
  matterId,
  threadId,
}: MessageListProps) => {
  const filteredMessages = useMemo(() => {
    return messages.filter(
      (message) =>
        message.message.type === 'ai' ||
        message.message.type === 'human' ||
        message.message.type === 'AIMessageChunk' ||
        message.message.type === 'HumanMessageChunk',
    );
  }, [messages]);

  const toolCalls: Record<string, TAgentToolMessage> = useMemo(() => {
    return messages.reduce(
      (acc, message) => {
        if (message.message.type === 'tool') {
          acc[message.message.tool_call_id] = message.message;
        }
        return acc;
      },
      {} as Record<string, TAgentToolMessage>,
    );
  }, [messages]);

  const sortedMessages = useMemo(() => {
    return filteredMessages.sort((a, b) => a.order - b.order);
  }, [filteredMessages]);

  const currentMessageIndex = useMemo(() => {
    if (sortedMessages.length === 0) {
      return 0;
    }

    return sortedMessages[sortedMessages.length - 1].order;
  }, [sortedMessages]);

  // Use auto-scroll hook - uses MutationObserver to detect content changes
  const { scrollRef, handleScroll } = useAutoScroll();

  if (isLoading) {
    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto p-4"
      >
        <div className="text-center p-4">
          <Text>Loading messages...</Text>
        </div>
      </div>
    );
  }

  if (sortedMessages.length === 0) {
    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto p-4"
      >
        <div className="text-center p-4">
          <Text>No messages yet. Start the conversation!</Text>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 overflow-auto px-4 py-6"
    >
      <div className="space-y-2 flex flex-col gap-3 max-w-4xl mx-auto">
        {sortedMessages.map((message) => (
          <Message
            key={message.message_id}
            message={message}
            toolMessages={toolCalls}
            matterId={matterId}
            threadId={threadId}
            isAwaitingResponse={
              message.order >= currentMessageIndex &&
              message.message.type === 'ai' &&
              message.message.tool_calls &&
              message.message.tool_calls.length > 0
            }
          />
        ))}
      </div>
    </div>
  );
};
